/**
 * Created by Yoyo Fang on 06/07/19.
 * Description:
 *
 * ------ maintenance history ------
 * 08/20/2019   Yoyo Fang   Wait until last processing task is finished, avoid running twice when prioritized.
 */
import { Observable } from 'rxjs';
import { take, map } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';

export class PriorityTaskQueueTask {
    private _finished = false;
    private _started = false;
    private _recorded: boolean;
    constructor(public id: string, private _task: (any?) => Observable<any>, recorded: boolean = false) {
        this._recorded = recorded;
    }

    isRecorded(): boolean {
        return this._recorded;
    }

    run(): Observable<any> {
        if (!this._started) {
            this._started = true;
            return this._task().pipe(
                take(1),
                map((result: any) => {
                    return result;
                }));
        }
    }

    started(): boolean {
        return this._started;
    }

    finished(): boolean {
        return this._finished;
    }
}
// all ran
// recorded
// error
export interface PriorityTaskQueueMessage {
    type: string;
    payload: any;
}

export class AllExecuted implements PriorityTaskQueueMessage {
    type = 'AllExecuted';
    // task ids in the order of finish time as payload
    constructor(public payload: string[]) {
    }
}

export class TaskExecuted implements PriorityTaskQueueMessage {
    type = 'TaskExecuted';
    // recorded task result as payload
    constructor(public taskId: string, public index: number, public payload: any) {
    }
}

export class EncounterError implements PriorityTaskQueueMessage {
    type = 'EncounterError';
    // error message as payload
    constructor(public payload: string) {
    }
}

export class PriorityTaskQueue {
    public isRunning = false;
    private _tasks: PriorityTaskQueueTask[] = [];
    private _tasksCount = 0;
    private _cancelList = {};
    private _resultChannel: Subject<PriorityTaskQueueMessage>;
    private _innerChannel: Subject<TaskExecuted>;
    private _innerSubscription: Subscription = null;
    private _finished: string[] = [];
    constructor(orderedTasks: PriorityTaskQueueTask[]) {
        this._tasks = [...orderedTasks];
        this._tasksCount = this._tasks.length;
        this._resultChannel = new Subject<PriorityTaskQueueMessage>();
    }

    cancel() {
        if (this._innerSubscription) {
            this._innerSubscription.unsubscribe();
            this._innerSubscription = null;
        }
        for (let i = this._tasksCount - 1; i >= 0; i--) {
            if (this._tasks[i]) { // pause tasks
                // const _started = this._tasks[i].started();
                if (!this._tasks[i].started() && this._cancelList[this._tasks[i].id] && !this._cancelList[this._tasks[i].id].closed) {
                    this._cancelList[this._tasks[i].id].unsubscribe();
                }
            }

        }
        this._cancelList = {};
        this.isRunning = false;
    }

    prioritize(taskId: string) {
        if (this._finished.length !== this._tasksCount) {
            this.cancel();
            let prioritizee: PriorityTaskQueueTask = null;
            let index = 0;
            for (; index < this._tasksCount; index++) {
                if (this._tasks[index].id === taskId) {
                    prioritizee = this._tasks[index];
                    break;
                }
            }
            let result = [];
            result = result.concat(this._tasks.slice(0, index)).concat(this._tasks.slice(index + 1, this._tasksCount));
            result.unshift(prioritizee);
            this._tasks = [...result];
            this.run();
        } else {
            this._resultChannel.next(new EncounterError('Cannot PriorityTaskQueue.prioritize() when all tasks are finished.'));
        }
    }

    getChannel(): Observable<PriorityTaskQueueMessage> {
        return this._resultChannel;
    }

    run() {
        if (!this.isRunning) {
            this.isRunning = true;
            this._innerChannel = new Subject<TaskExecuted>();
            this._innerSubscription = this._innerChannel.subscribe((result: TaskExecuted) => {
                const nextIndex = result.index + 1;
                if (nextIndex < this._tasksCount) {
                    this._run(nextIndex);
                } else {
                    this._innerSubscription.unsubscribe();
                    this._innerSubscription = null;
                    this._resultChannel.next(new AllExecuted(this._finished));
                }
            });
            this._run(0);
        } else {
            this._resultChannel.next(new EncounterError('PriorityTaskQueue.run() is called while queue is busy running.'));
        }
    }

    private _run(index = 0): void {
        if (index >= 0 && index < this._tasksCount && this._tasks[index]) {
            const item = this._tasks[index];
            if (!item.started()) {
                this._cancelList[item.id] = item.run().pipe(take(1)).subscribe((result) => {
                    this._finished.push(item.id);
                    this._resultChannel.next(new TaskExecuted(item.id, index, result));
                    this._innerChannel.next(new TaskExecuted(item.id, index, null));
                });
            } else if (item.finished()) {
                this._innerChannel.next(new TaskExecuted(item.id, index, null));
            } else {
                // started not finished means current loop is inited by prioritize
                // and this item is the leftover of last loop, wait til it finish to continue current loop
                this._innerChannel.pipe(take(1)).subscribe((result: TaskExecuted) => {
                    this._finished.push(item.id);
                    this._resultChannel.next(new TaskExecuted(item.id, index, result));
                    this._innerChannel.next(new TaskExecuted(item.id, index, null));
                });
            }
        } else {
            this._resultChannel.next(new EncounterError('Cannot run tasks[' + index + '] of all ' + this._tasksCount + ' tasks.'));
        }
    }
}
